import * as _ from "lodash";

import { IAction } from "../model/IAction";
import { IProcessVariable } from "../model/IProcessData";
import { ValidationError, ValidationErrorType } from "../model/ValidationData";
import { matchesEnvVarOrTriggerDataRef, VariableNameHasOnlyValidChars } from "./ValidateProcess";
import { certificateReferenceRegex } from "../../shared/model/CertificateReference";

export enum ProcessVarValidationErrorType {
    Name,
    Value,
    Reference,
}

export interface ValidateProcessVariablesAction extends IAction {
    processVariableValidationErrors: ValidationError[]
}

export function validateProcessVariables(processVariables: IProcessVariable[], environmentVariables: string[]): ValidationError[] {
    const result = processVariables.map(processVariable =>
        validateProcessVariableName(processVariable).concat(
            validateProcessVariableValue(processVariable, environmentVariables)))
        .flat();

    const variablesGroupedByname = _.groupBy(processVariables, function(processVariable){
        return processVariable.name.toLowerCase();
    });

    for (const duplicateProcessVariable of _.flatten(Object.values(variablesGroupedByname).filter(group => group.length > 1).map(group => group.slice(1)))) {
        result.push(new ValidationError(ValidationErrorType.DuplicateVariableName, GetProcessVariableErrorId(duplicateProcessVariable, ProcessVarValidationErrorType.Name)))
    }

    return result;
}

export function validateProcessVariableName(processVariable: IProcessVariable): ValidationError[] {
    const result: ValidationError[] = [];

    if (processVariable.name == null || processVariable.name === "") {
        result.push(new ValidationError(ValidationErrorType.VariableNameMissing, GetProcessVariableErrorId(processVariable, ProcessVarValidationErrorType.Name)))
    } else if (!VariableNameHasOnlyValidChars(processVariable.name)) {
        result.push(new ValidationError(ValidationErrorType.InvalidVariableName, GetProcessVariableErrorId(processVariable, ProcessVarValidationErrorType.Name), undefined, undefined, 'Invalid character in variable name'));
    }

    return result;
}

export function validateProcessVariableValue(processVariable: IProcessVariable, environmentVariables: string[], allowEmpty: boolean = false, allowMissingEnvVar: boolean = false): ValidationError[] {
    const result: ValidationError[] = [];

    if (allowEmpty && processVariable.displayValue === "") {
        return result;
    }

    if (processVariable.value.search(certificateReferenceRegex) >= 0) {
        result.push(
            new ValidationError(
                ValidationErrorType.InvalidReference,
                GetProcessVariableErrorId(processVariable, ProcessVarValidationErrorType.Reference),
                undefined,
                undefined,
                "Certificate references (#cert) are not supported in process variables."
            )
        );
        return result;
    }

    if (processVariable.value.toLowerCase().startsWith("#env.") && processVariable.value.toLowerCase().slice("#env.".length).trim().length > 0) {
        if (allowMissingEnvVar) {
            return result;
        }

        if (!environmentVariables.find((r) => matchesEnvVarOrTriggerDataRef(r, processVariable.value.substring(processVariable.value.indexOf(".") + 1)))) {
            result.push(
                new ValidationError(
                    ValidationErrorType.InvalidReference,
                    GetProcessVariableErrorId(processVariable, ProcessVarValidationErrorType.Reference),
                    undefined,
                    processVariable.value
                )
            );
        }
    }
    else if (processVariable.mode === 'toggle' && !(processVariable.value === 'true' || processVariable.value === 'false')) {
        result.push(
            new ValidationError(
                ValidationErrorType.InvalidProcessVariableValueToggle,
                GetProcessVariableErrorId(processVariable, ProcessVarValidationErrorType.Value),
                undefined,
                processVariable.value
            )
        );
    }
    else if (processVariable.mode === 'integer' && (!processVariable.value || Number.isNaN(Number(processVariable.value)))) {
        result.push(
            new ValidationError(
                ValidationErrorType.InvalidProcessVariableValueInteger,
                GetProcessVariableErrorId(processVariable, ProcessVarValidationErrorType.Value),
                undefined,
                processVariable.value
            )
        );
    }
    else if (processVariable.mode === 'json' && !isJSONParsable(processVariable)) {
        result.push(
            new ValidationError(
                ValidationErrorType.InvalidProcessVariableValue,
                GetProcessVariableErrorId(processVariable, ProcessVarValidationErrorType.Value)
            )
        );
    }

    const processVariableMaxLength = 10000;
    if (processVariable.value.length > processVariableMaxLength) {
        const message = `Value may not exceed ${processVariableMaxLength} characters.`
        result.push(new ValidationError(
            ValidationErrorType.InvalidProcessVariableValue,
            GetProcessVariableErrorId(processVariable, ProcessVarValidationErrorType.Value),
            undefined,
            undefined,
            message)
        );
    }
    return result;
}

const isJSONParsable = (processVariable: IProcessVariable): boolean => {
    try {
        JSON.parse(processVariable.value);
    } catch (e) {
        return false;
    }
    return true;
}

export function GetProcessVariableErrorId(processVariable: IProcessVariable, errorType: ProcessVarValidationErrorType) {
    return `${processVariable.id}-${errorType}`;
}
